import { TOKEN_BLACKLIST } from 'config/constants/info'
import { gql } from 'graphql-request'
import { useEffect, useState, useRef } from 'react'
import { infoClient } from 'utils/graphql'
import { getDeltaTimestamps } from 'views/Info/utils/infoQueryHelpers'

interface TopPoolsResponse {
  pairs: {
    id: string
  }[]
}

/**
 * Fetch all pools - removed restrictive filters to show all pools
 */
const fetchTopPools = async (timestamp24hAgo: number): Promise<string[]> => {
  try {
    // Query pairs directly instead of pairDayDatas to get all pools, not just recent ones
    const query = gql`
      query topPools($blacklist: [String!]) {
        pairs(
          first: 1000
          where: { token0_not_in: $blacklist, token1_not_in: $blacklist }
          orderBy: reserveUSD
          orderDirection: desc
        ) {
          id
        }
      }
    `
    const data = await infoClient.request<TopPoolsResponse>(query, { blacklist: TOKEN_BLACKLIST })
    return data.pairs.map((p) => p.id)
  } catch (error) {
    console.error('Failed to fetch top pools', error)
    // Fallback to pairDayDatas if pairs query fails
    try {
      const fallbackQuery = gql`
        query topPoolsFallback($blacklist: [String!], $timestamp24hAgo: Int) {
          pairDayDatas(
            first: 1000
            where: { date_gt: $timestamp24hAgo }
            orderBy: dailyVolumeUSD
            orderDirection: desc
          ) {
            id
          }
        }
      `
      const fallbackData = await infoClient.request<{ pairDayDatas: { id: string }[] }>(fallbackQuery, {
        blacklist: TOKEN_BLACKLIST,
        timestamp24hAgo,
      })
      return fallbackData.pairDayDatas.map((p) => p.id.split('-')[0])
    } catch (fallbackError) {
      console.error('Failed to fetch pools with fallback query', fallbackError)
      return []
    }
  }
}

/**
 * Fetch top addresses by volume
 */
const useTopPoolAddresses = (): string[] => {
  const [topPoolAddresses, setTopPoolAddresses] = useState<string[]>([])
  const [timestamp24hAgo] = getDeltaTimestamps()
  const lastFetchedTimestamp = useRef<number | null>(null)

  useEffect(() => {
    // Only fetch if we haven't fetched for this timestamp yet
    if (lastFetchedTimestamp.current !== timestamp24hAgo) {
      const fetch = async () => {
        try {
          const addresses = await fetchTopPools(timestamp24hAgo)
          setTopPoolAddresses(addresses)
          lastFetchedTimestamp.current = timestamp24hAgo
        } catch (error) {
          console.error('Error in useTopPoolAddresses fetch:', error)
          // Still mark as fetched to prevent infinite retries
          lastFetchedTimestamp.current = timestamp24hAgo
        }
      }
      fetch()
    }
  }, [timestamp24hAgo])

  return topPoolAddresses
}

export default useTopPoolAddresses
